"use client";

import {
  ArrowUpDown,
  Check,
  ChevronDown,
  PackageSearch,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { type Product } from "@/lib/products";
import { getPublicProductCardsByIds } from "@/lib/supabase/public-data";
import {
  SEARCH_PUBLIC_MAX_LIMIT,
  searchPublicProducts,
  type SearchResult,
} from "@/lib/supabase/search";
import { type HomeProductType } from "@/lib/homeCatalog";
import { type SearchSort } from "@/lib/searchCatalog";
import { withTimeout } from "@/lib/withTimeout";
import { browseFeedContext, withFeedContext } from "@/lib/feedContext";
import { type useCustomerGroup } from "@/context/CustomerContext";
import { HomeProductCard } from "@/components/home/HomeProductCard";
import { CategoryRow } from "@/components/home/CategoryRow";
import { BrandRow } from "@/components/home/BrandRow";
import { HomeProductCardSkeleton } from "@/components/home/HomeProductCardSkeleton";
import { FacetChipRow } from "@/components/search/FacetChipRow";
import {
  LoadMoreSentinel,
  useInfiniteSentinel,
} from "@/components/admin/infiniteScroll";
import {
  useRememberedList,
  useRememberedState,
  useRememberedValue,
} from "@/components/admin/AdminListMemory";
import { rememberedChoice, rememberedCount } from "@/lib/adminListMemory";
import { restoreChunks, restoreLoadCount } from "@/lib/listRestore";

const PAGE_SIZE = 24;

const TYPE_OPTIONS: Array<{ value: HomeProductType | null; label: string }> = [
  { value: null, label: "Semua" },
  { value: "preorder", label: "Pre-Order" },
  { value: "ready_stock", label: "Ready" },
];
/** Nilai field memori "type" ("" = Semua). */
const TYPE_MEMORY_IDS = ["", "preorder", "ready_stock"] as const;

// Tanpa kata kunci, "relevance" tak berguna → tawarkan Terpopuler (default) dst.
const SORT_OPTIONS: SearchSort[] = ["popular", "newest", "closing", "name"];
const SORT_LABELS: Record<SearchSort, string> = {
  relevance: "Paling Relevan",
  popular: "Terpopuler",
  newest: "Terbaru",
  closing: "Segera Ditutup",
  name: "Nama A-Z",
};

export type BrowseDims = {
  brands: string[];
  categories: string[];
  segments: string[];
};

/** Toggle nilai di array faset (multi-pilih, case-insensitive). */
function toggleValue(list: string[], name: string): string[] {
  const lower = name.toLowerCase();
  return list.some((v) => v.toLowerCase() === lower)
    ? list.filter((v) => v.toLowerCase() !== lower)
    : [...list, name];
}

/**
 * Browse katalog SISI-SERVER (RPC `search_public_products`, query kosong) yang
 * DI-SHARE halaman Kategori & halaman Merek. SATU dimensi (merek/kategori/segmen)
 * "dikunci" pemanggil lewat `lockedBrand/Category/Segment`; dimensi yang TIDAK
 * dikunci muncul sebagai faset MULTI-pilih yang bisa di-drill (mig 169). Sub-tab
 * Tipe + Urutkan (default Terpopuler) + infinite scroll. Drill awal di-seed dari
 * URL (`seedBrands/Categories/Segments`, #3 shareable). Hidrasi kartu +
 * race-safety = pola `SearchClient`. State direset bersih lewat `key`; URL
 * disinkron via `buildHref` (replaceState — tanpa refetch RSC).
 *
 * Memori posisi daftar: bila dibungkus `<AdminListMemory ssr>` (pemanggil),
 * drill, tipe, urutan, dan jumlah produk termuat ikut pulih saat Kembali dari
 * halaman produk. Drill WAJIB ikut field, bukan dibaca ulang dari URL: saat
 * Kembali, Next memulihkan pohon RSC lama — props `seed*` milik URL AWAL,
 * bukan URL hasil `replaceState` — lalu efek sinkron URL di bawah menulis ulang
 * URL ke drill basi itu (terbukti di Chromium 2026-10-28).
 */
export function CatalogBrowse({
  lockedBrand = null,
  lockedCategory = null,
  lockedSegment = null,
  seedBrands = [],
  seedCategories = [],
  seedSegments = [],
  initialType = null,
  query = "",
  facetStyle = "chips",
  group,
  now,
  buildHref,
  buildProductHref,
  storeId = null,
  brandLogos,
}: {
  lockedBrand?: string | null;
  lockedCategory?: string | null;
  lockedSegment?: string | null;
  /** Drill awal dari URL (dimensi yang TIDAK dikunci). */
  seedBrands?: string[];
  seedCategories?: string[];
  seedSegments?: string[];
  initialType?: HomeProductType | null;
  /** Kata kunci pencarian teks (opsional). Kosong = tak memfilter (perilaku
   *  lama halaman Kategori/Merek). Katalog Reseller (mig 216) meneruskan input
   *  pencarian di sini → saat ada kata kunci hasil diurut Paling Relevan. */
  query?: string;
  /** Tampilan faset kategori/merek. `"chips"` (default) = FacetChipRow multi-
   *  pilih (halaman Kategori/Merek/Cari — TAK berubah). `"home"` = gaya BERANDA
   *  (tile ikon Kategori + drill segmen + chip Merek), SINGLE-pilih — dipakai
   *  Katalog Reseller agar mirip beranda toko. Opt-in murni; tak menyentuh
   *  pemanggil lain. */
  facetStyle?: "chips" | "home";
  group: ReturnType<typeof useCustomerGroup>;
  now: Date;
  /** URL untuk replaceState dari dimensi efektif (locked + drill aktif). */
  buildHref: (dims: BrowseDims) => string;
  /** Tujuan tap kartu — default `/product/[slug]`; Katalog Reseller (mig 216)
   *  memakai `/toko/[kode]/product/[slug]` agar navigasi tetap di katalog. */
  buildProductHref?: (product: Product) => string;
  /** MULTI-TENANT FASE 4: toko hasil resolusi Host (dari server page). */
  storeId?: string | null;
  /** Peta logo merek (`masterLogoMap`) untuk chip Merek gaya beranda
   *  (`facetStyle="home"`). Opsional — tanpa peta chip tetap ber-inisial. */
  brandLogos?: ReadonlyMap<string, string>;
}) {
  // Dimensi yang BISA dipilih = kebalikan dari yang dikunci (di-seed dari URL).
  const [drillBrands, setDrillBrands] = useRememberedList("drill.brands", seedBrands);
  const [drillCategories, setDrillCategories] = useRememberedList(
    "drill.categories",
    seedCategories
  );
  const [drillSegments, setDrillSegments] = useRememberedList(
    "drill.segments",
    seedSegments
  );
  const [typeMemory, setTypeMemory] = useRememberedState<
    (typeof TYPE_MEMORY_IDS)[number]
  >("type", initialType ?? "", (raw) => rememberedChoice(raw, TYPE_MEMORY_IDS));
  const type: HomeProductType | null = typeMemory === "" ? null : typeMemory;
  const setType = useCallback(
    (next: HomeProductType | null) => setTypeMemory(next ?? ""),
    [setTypeMemory]
  );
  const [sort, setSort] = useRememberedState<SearchSort>("sort", "popular", (raw) =>
    rememberedChoice(raw, SORT_OPTIONS)
  );
  const [sortOpen, setSortOpen] = useState(false);

  const brands = lockedBrand ? [lockedBrand] : drillBrands;
  const categories = lockedCategory ? [lockedCategory] : drillCategories;
  const segments = lockedSegment ? [lockedSegment] : drillSegments;
  // Daftar asal untuk feed geser halaman produk (`?dari=`): pembeli yang
  // membuka produk dari halaman merek/segmen/pencarian menggeser di dalam
  // daftar ini dulu. Hanya dimensi yang DIKUNCI (atau kata kunci) — drill faset
  // tak ikut, supaya tautannya stabil selagi pembeli memilah chip.
  const feedContext = browseFeedContext({
    query,
    lockedBrand,
    lockedCategory,
    lockedSegment,
  });

  const [ids, setIds] = useState<string[]>([]);
  // Jumlah produk termuat (memori daftar): muatan PERTAMA sesudah pulih
  // mengambil ulang 0..N-1 bertahap ≤60 (plafon RPC) supaya kartu jangkar ada.
  const restoredCount = useRememberedValue("count", ids.length, (raw) =>
    rememberedCount(raw, PAGE_SIZE)
  );
  const [facets, setFacets] = useState({
    brands: [] as string[],
    categories: [] as string[],
    segments: [] as string[],
  });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cards, setCards] = useState<Map<string, Product>>(() => new Map());

  const idsRef = useRef<string[]>([]);
  const reqSeqRef = useRef(0);
  const inFlightRef = useRef(false);
  const pendingRef = useRef<Set<string>>(new Set());

  const hydrate = useCallback(
    (list: string[]) => {
      const missing = list.filter(
        (id) => !cards.has(id) && !pendingRef.current.has(id)
      );
      if (!missing.length) return;
      missing.forEach((id) => pendingRef.current.add(id));
      withTimeout(getPublicProductCardsByIds(missing, storeId))
        .then((fetched) => {
          setCards((prev) => {
            const next = new Map(prev);
            fetched.forEach((card) => next.set(card.id, card));
            return next;
          });
        })
        .catch(() => {
          // Gagal/timeout: kartu tetap skeleton; effect coba lagi saat re-render.
        })
        .finally(() => {
          missing.forEach((id) => pendingRef.current.delete(id));
        });
    },
    [cards, storeId]
  );

  /** `count` > satu halaman hanya pada muatan pertama sesudah pulih. */
  const runSearch = useCallback(
    async (reset: boolean, count: number = PAGE_SIZE) => {
      const seq = (reqSeqRef.current += 1);
      inFlightRef.current = true;
      if (reset) setLoading(true);
      else setLoadingMore(true);
      try {
        const chunks = reset
          ? restoreChunks(count, SEARCH_PUBLIC_MAX_LIMIT)
          : [{ offset: idsRef.current.length, limit: PAGE_SIZE }];
        let res: SearchResult | null = null;
        const fetched: string[] = [];
        for (const chunk of chunks) {
          const part: SearchResult = await withTimeout(
            searchPublicProducts({
              query: query.trim(),
              brands,
              categories,
              segments,
              // Ada kata kunci → urut Paling Relevan (word_similarity di RPC);
              // tanpa kata kunci pakai pilihan Urutkan pengguna.
              type,
              sort: query.trim() ? "relevance" : sort,
              storeId,
              limit: chunk.limit,
              offset: chunk.offset,
            })
          );
          if (seq !== reqSeqRef.current) return;
          res = part;
          fetched.push(...part.ids.filter((id) => !fetched.includes(id)));
          hydrate(part.ids);
          if (part.ids.length < chunk.limit) break;
        }
        if (!res) return;
        const last = res;
        setTotal(last.total);
        setFacets({
          brands: last.brands,
          categories: last.categories,
          segments: last.segments,
        });
        setIds((prev) => {
          const next = reset
            ? fetched
            : [...prev, ...fetched.filter((id) => !prev.includes(id))];
          idsRef.current = next;
          return next;
        });
      } catch {
        if (seq === reqSeqRef.current && reset) {
          setIds([]);
          idsRef.current = [];
          setTotal(0);
        }
      } finally {
        if (seq === reqSeqRef.current) {
          setLoading(false);
          setLoadingMore(false);
          inFlightRef.current = false;
        }
      }
    },
    [brands, categories, segments, type, sort, query, hydrate, storeId]
  );

  // Key string kriteria → effect jalan saat himpunan berubah (array stabil ref).
  const critKey = `${brands.join(",")}|${categories.join(",")}|${segments.join(
    ","
  )}|${type ?? ""}|${sort}|${query.trim()}|${storeId ?? ""}`;
  // Jumlah pulihan berlaku hanya untuk kriteria saat pulih; begitu kriteria
  // berganti kuncinya di-null-kan (efek ganda StrictMode tetap memakainya).
  const [restoreLoad] = useState(() => ({
    count: restoreLoadCount(restoredCount, PAGE_SIZE),
    key: critKey,
  }));
  const restoreKeyRef = useRef<string | null>(restoreLoad.key);
  useEffect(() => {
    const restoringFirst = restoreKeyRef.current === critKey;
    if (!restoringFirst) restoreKeyRef.current = null;
    runSearch(true, restoringFirst ? restoreLoad.count : PAGE_SIZE);
  }, [critKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sinkron URL (shareable) — kunci tetap, sertakan drill aktif.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.history.replaceState(
      null,
      "",
      buildHref({ brands, categories, segments })
    );
  }, [critKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = useCallback(() => {
    if (!inFlightRef.current) runSearch(false);
  }, [runSearch]);

  const hasMore = ids.length < total;
  const sentinelRef = useInfiniteSentinel(
    loadMore,
    hasMore && !loading && !loadingMore
  );

  const toggle = (
    setter: (updater: (list: string[]) => string[]) => void,
    name: string
  ) => setter((list) => toggleValue(list, name));

  const hasDrill =
    drillBrands.length > 0 ||
    drillCategories.length > 0 ||
    drillSegments.length > 0 ||
    type !== null;

  return (
    <>
      <div className="flex items-center justify-between gap-2 bg-white px-3 py-2">
        <div className="no-scrollbar flex min-w-0 gap-0.5 overflow-x-auto rounded-full bg-neutral-100 p-0.5">
          {TYPE_OPTIONS.map((option) => {
            const isActive = type === option.value;
            return (
              <button
                key={option.label}
                type="button"
                onClick={() => setType(option.value)}
                className={`h-7 shrink-0 rounded-full px-3 text-[11px] font-bold transition ${
                  isActive ? "bg-white text-shopee shadow-sm" : "text-muted"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => setSortOpen((open) => !open)}
            className="flex h-8 items-center gap-1 rounded-full border border-neutral-200 bg-white px-3 text-[11px] font-bold text-ink"
          >
            <ArrowUpDown size={13} className="text-muted" />
            {SORT_LABELS[sort]}
            <ChevronDown size={13} className="text-muted" />
          </button>
          {sortOpen ? (
            <>
              <button
                type="button"
                aria-hidden
                onClick={() => setSortOpen(false)}
                className="fixed inset-0 z-10 cursor-default"
              />
              <div className="absolute right-0 z-20 mt-1 w-40 overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-lg">
                {SORT_OPTIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      setSort(option);
                      setSortOpen(false);
                    }}
                    className={`flex w-full items-center justify-between px-3 py-2 text-xs font-semibold ${
                      sort === option ? "text-shopee" : "text-ink"
                    }`}
                  >
                    {SORT_LABELS[option]}
                    {sort === option ? <Check size={14} /> : null}
                  </button>
                ))}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* Dimensi yang DIKUNCI pemanggil tidak ditampilkan sebagai faset; yang
          bisa di-drill ditampilkan. `facetStyle="home"` (Katalog Reseller) →
          gaya BERANDA (tile Kategori + drill segmen + chip Merek, single-pilih);
          default `"chips"` (Kategori/Merek/Cari) → FacetChipRow multi-pilih. */}
      {facetStyle === "home" ? (
        <>
          {!lockedCategory ? (
            <CategoryRow
              categories={facets.categories}
              active={drillCategories[0] ?? null}
              onSelect={(name) => {
                // Single-pilih + reset segmen saat kategori berganti (cermin beranda).
                setDrillCategories(name ? [name] : []);
                setDrillSegments([]);
              }}
              highlightActive
              segments={lockedSegment ? [] : facets.segments}
              activeSegment={drillSegments[0] ?? null}
              onSelectSegment={(name) => setDrillSegments(name ? [name] : [])}
            />
          ) : null}
          {/* Fallback: kategori dikunci (tak dipakai Katalog Reseller) → segmen
              tetap bisa dipilih lewat chip. */}
          {lockedCategory && !lockedSegment ? (
            <FacetChipRow
              title="Segmen"
              items={facets.segments}
              active={drillSegments}
              onToggle={(name) => toggle(setDrillSegments, name)}
            />
          ) : null}
          {!lockedBrand ? (
            <BrandRow
              brands={facets.brands}
              logos={brandLogos}
              active={drillBrands[0] ?? null}
              // Tap merek aktif lagi = lepas (BrandRow tanpa opsi "Semua").
              // BrandRow tak pernah mengirim null, tapi tipenya string|null.
              onSelect={(name) => {
                if (!name) return;
                setDrillBrands(drillBrands[0] === name ? [] : [name]);
              }}
              highlightActive
            />
          ) : null}
        </>
      ) : (
        <>
          {!lockedCategory ? (
            <FacetChipRow
              title="Kategori"
              items={facets.categories}
              active={drillCategories}
              onToggle={(name) => toggle(setDrillCategories, name)}
            />
          ) : null}
          {!lockedSegment ? (
            <FacetChipRow
              title="Segmen"
              items={facets.segments}
              active={drillSegments}
              onToggle={(name) => toggle(setDrillSegments, name)}
            />
          ) : null}
          {!lockedBrand ? (
            <FacetChipRow
              title="Merek"
              items={facets.brands}
              active={drillBrands}
              onToggle={(name) => toggle(setDrillBrands, name)}
            />
          ) : null}
        </>
      )}

      <section className="px-3 pb-6 pt-3">
        <p className="mb-2 text-[11px] font-medium text-muted">
          {loading && !ids.length ? "Memuat…" : `${total} produk`}
        </p>

        {loading && !ids.length ? (
          <div className="grid grid-cols-2 gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <HomeProductCardSkeleton key={i} />
            ))}
          </div>
        ) : ids.length ? (
          <>
            <div className="grid grid-cols-2 gap-2">
              {ids.map((id, cardIndex) => {
                const card = cards.get(id);
                return card ? (
                  <HomeProductCard
                    key={id}
                    product={card}
                    group={group}
                    now={now}
                    priority={cardIndex < 4}
                    listAnchor={`product:${id}`}
                    href={withFeedContext(
                      buildProductHref ? buildProductHref(card) : `/product/${card.slug}`,
                      feedContext
                    )}
                  />
                ) : (
                  <HomeProductCardSkeleton key={id} listAnchor={`product:${id}`} />
                );
              })}
            </div>
            {hasMore ? <LoadMoreSentinel sentinelRef={sentinelRef} /> : null}
          </>
        ) : (
          <div className="flex flex-col items-center gap-3 rounded-xl bg-white px-4 py-10 text-center">
            <PackageSearch size={40} className="text-neutral-300" />
            <p className="text-sm font-bold text-ink">
              Belum ada produk yang cocok
            </p>
            {hasDrill ? (
              <button
                type="button"
                onClick={() => {
                  setDrillBrands([]);
                  setDrillCategories([]);
                  setDrillSegments([]);
                  setType(null);
                }}
                className="grid h-9 place-items-center rounded-full border border-shopee bg-white px-5 text-xs font-bold text-shopee"
              >
                Lepas Filter
              </button>
            ) : null}
          </div>
        )}
      </section>
    </>
  );
}
